import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSupabaseService } from '../lib/supabase-services';
import { TaxExpense, TaxExpenseInput } from '../types/database';

export const taxExpensesKeys = {
  all: ['taxExpenses'] as const,
  byTaxYear: (taxYearStart: number) => [...taxExpensesKeys.all, taxYearStart] as const,
};

export const useTaxExpensesQuery = (taxYearStart: number) => {
  return useQuery({
    queryKey: taxExpensesKeys.byTaxYear(taxYearStart),
    queryFn: () => getSupabaseService().getTaxExpenses(taxYearStart),
  });
};

/** Keep the year's list in sync with a mutation result without waiting for a refetch. */
const useExpenseCacheUpdater = () => {
  const queryClient = useQueryClient();
  return (taxYearStart: number, update: (current: TaxExpense[]) => TaxExpense[]) => {
    queryClient.setQueryData<TaxExpense[]>(taxExpensesKeys.byTaxYear(taxYearStart), (current) =>
      update(current ?? [])
    );
    void queryClient.invalidateQueries({ queryKey: taxExpensesKeys.byTaxYear(taxYearStart) });
  };
};

export const useAddTaxExpenseMutation = () => {
  const updateCache = useExpenseCacheUpdater();
  return useMutation({
    mutationFn: (expense: TaxExpenseInput) => getSupabaseService().addTaxExpense(expense),
    onSuccess: (saved) => updateCache(saved.tax_year_start, (list) => [...list.filter((e) => e.id !== saved.id), saved]),
  });
};

export const useUpdateTaxExpenseMutation = () => {
  const updateCache = useExpenseCacheUpdater();
  return useMutation({
    mutationFn: ({ id, expense }: { id: number; expense: TaxExpenseInput }) =>
      getSupabaseService().updateTaxExpense(id, expense),
    onSuccess: (saved) =>
      updateCache(saved.tax_year_start, (list) => list.map((e) => (e.id === saved.id ? saved : e))),
  });
};

/** Removes the row immediately, then restores it if the server delete fails. */
export const useDeleteTaxExpenseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: number; taxYearStart: number }) => getSupabaseService().deleteTaxExpense(id),
    retry: false,
    onMutate: async ({ id, taxYearStart }) => {
      const key = taxExpensesKeys.byTaxYear(taxYearStart);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TaxExpense[]>(key);
      queryClient.setQueryData<TaxExpense[]>(key, (current) => (current ?? []).filter((e) => e.id !== id));
      return { previous };
    },
    onError: (_error, { taxYearStart }, context) => {
      if (context?.previous) queryClient.setQueryData(taxExpensesKeys.byTaxYear(taxYearStart), context.previous);
    },
    onSettled: (_data, _error, { taxYearStart }) => {
      void queryClient.invalidateQueries({ queryKey: taxExpensesKeys.byTaxYear(taxYearStart) });
    },
  });
};
